import type {
  LlmProfile,
  LlmProfileAdminOverview,
  LlmProfileAdminOverviewItem,
  LlmProfileDeletedAdminOverviewItem,
  LlmProfileModelStatus,
} from './types';

/**
 * The pure layer behind the LLM profiles admin grid (spec 136 data-model.md § 2).
 *
 * Named functions outside the render path (constitution §2.3, FR-012), and the
 * single place a reader checks what "implicit default" means or how spend orders.
 * Nothing here builds a parallel copy of server state (§4): `LlmProfileRow` is an
 * alias for the contract item, and these functions return counts, verdicts and
 * reorderings of the same objects.
 */

/**
 * One grid row — an alias, not a new object graph (research R5).
 *
 * The single read already returns one row per profile carrying every field the grid
 * shows, so a derived row object would copy server state into a second shape for no
 * gain. The per-model count is the only thing the server does not send, and it comes
 * back as a `Map` beside the items rather than as a field on them.
 */
export type LlmProfileRow = LlmProfileAdminOverviewItem;

/**
 * A deleted profile that still has usage (spec 258 FR-004): a name, its spend and its
 * tokens, and nothing to act on. Kept apart from {@link LlmProfileRow} so the counts,
 * the filters and every control keep reading live profiles only.
 */
export type DeletedLlmProfileRow = LlmProfileDeletedAdminOverviewItem;

/** One line of the grid: a live profile or a deleted one. */
export type LlmProfileGridEntry = LlmProfileRow | DeletedLlmProfileRow;

/** The overview's items, live and deleted apart, each in the order the server sent. */
export function splitOverviewItems(overview: LlmProfileAdminOverview): {
  live: LlmProfileRow[];
  deleted: DeletedLlmProfileRow[];
} {
  const live: LlmProfileRow[] = [];
  const deleted: DeletedLlmProfileRow[] = [];
  for (const item of overview.items) {
    if (item.deleted) deleted.push(item);
    else live.push(item);
  }
  return { live, deleted };
}

/** The columns the grid offers an order for (FR-003, ui-contract.md "The table"). */
export const PROFILE_SORT_KEYS = [
  'name',
  'provider',
  'model',
  'spend',
  'workspaces',
  'workflows',
] as const;

export type ProfileSortKey = (typeof PROFILE_SORT_KEYS)[number];

export interface LlmProfilesSort {
  key: ProfileSortKey;
  dir: 'asc' | 'desc';
}

/**
 * How many profiles in the whole response share each `model` (FR-009).
 *
 * One pass, over the full response rather than the filtered rows: "is this the only
 * profile for its model" is a fact about the registry, not about what the reader has
 * searched for. Feeds `defaultKind` and `SetDefaultProfileControl`'s existing
 * `siblingCount` prop.
 */
export function profileCountsByModel(
  items: readonly LlmProfileRow[],
): ReadonlyMap<string, number> {
  const counts = new Map<string, number>();
  for (const item of items) {
    counts.set(item.model, (counts.get(item.model) ?? 0) + 1);
  }
  return counts;
}

/**
 * Whether a profile's default-ness was chosen or merely uncontested (FR-009).
 *
 * The service's `is_default` already folds the implicit case in, so the distinction
 * is only recoverable by counting profiles per `model` across the response — which
 * is also what makes it self-correcting: deleting the second-to-last profile for a
 * model flips the survivor `'explicit'` → `'implicit'` on the next fetch with no
 * extra code.
 */
export function defaultKind(
  item: LlmProfileRow,
  counts: ReadonlyMap<string, number>,
): 'explicit' | 'implicit' | 'none' {
  if (!item.is_default) return 'none';
  // A model absent from the map cannot happen for an item the map was built from,
  // but 1 is the honest fallback: the item itself is that one profile.
  return (counts.get(item.model) ?? 1) > 1 ? 'explicit' : 'implicit';
}

/**
 * Case-insensitive substring match over profile name, `model` and provider name
 * (FR-012), with the query trimmed and an empty query matching everything.
 *
 * Deliberately different from the grouped list's filter, which matched
 * name/slug/model (`filterModelGroups`, deleted with the grouping in spec 136):
 * `slug` is **out** (it is an identifier, not something an administrator types) and
 * the provider's name is **in** (FR-012 and US2 both name it).
 */
export function matchesProfileQuery(
  item: LlmProfileRow,
  query: string,
): boolean {
  const needle = query.trim().toLocaleLowerCase();
  if (needle === '') return true;
  return (
    item.name.toLocaleLowerCase().includes(needle) ||
    item.model.toLocaleLowerCase().includes(needle) ||
    item.ai_provider.name.toLocaleLowerCase().includes(needle)
  );
}

/** Ties read A to Z, in a fixed direction — see the comment in `sortProfileRows`. */
function byName(a: LlmProfileGridEntry, b: LlmProfileGridEntry): number {
  return (
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }) ||
    entryKey(a).localeCompare(entryKey(b))
  );
}

/**
 * A grid entry's identity: a live profile's slug, a deleted one's key (its slug, or
 * its id when the runs recorded no slug). Prefixed so the two can never collide as
 * React keys.
 */
export function entryKey(entry: LlmProfileGridEntry): string {
  return entry.deleted ? `deleted:${entry.key}` : entry.slug;
}

/**
 * Orders two money amounts **as strings**, never as floats (FR-005, research R6).
 *
 * `spend.amount` arrives as the exact decimal the service holds in `NUMERIC`, and
 * `Number()`/`parseFloat` is the single step that reintroduces the imprecision the
 * backend went to trouble to avoid — `money.ts` documents a demonstrated case of it
 * (`Math.floor(19.99 * 100)` → `1998`). So: left-pad the integer parts to a common
 * width, right-pad the fraction parts to a common width, and compare lexically.
 * Equal-length decimal strings of digits order identically to their numeric values,
 * which is what makes `"9.5" < "10.00"` and `"0.3" > "0.25"` come out right where a
 * naive string compare would not.
 *
 * Amounts here are non-negative — a sum of costs — and that is exactly what makes
 * padding sufficient. A signed or otherwise unparseable amount is **not** ordered by
 * padding, because the comparator would reverse it and a reader has no way to see
 * that from the table; it sinks to the bottom instead (see `unparseableLast`).
 *
 * By the time this runs, either both amounts parse or neither does — `unparseableLast`
 * has already separated a parseable amount from an unparseable one — so a `null` here
 * means "two amounts neither of which can be ordered", which is a tie for the
 * `byName` tie-break to settle.
 */
function compareAmounts(a: string, b: string): number {
  const left = splitAmount(a);
  const right = splitAmount(b);
  if (left === null || right === null) return 0;
  const wholeWidth = Math.max(left.whole.length, right.whole.length);
  const fractionWidth = Math.max(left.fraction.length, right.fraction.length);
  const padded = (part: { whole: string; fraction: string }) =>
    `${part.whole.padStart(wholeWidth, '0')}.${part.fraction.padEnd(fractionWidth, '0')}`;
  // A plain `<`/`>` compare, not `localeCompare`: these are digits, and a locale
  // has no business deciding how two numbers order.
  const paddedLeft = padded(left);
  const paddedRight = padded(right);
  if (paddedLeft < paddedRight) return -1;
  if (paddedLeft > paddedRight) return 1;
  return 0;
}

/**
 * The digits of a non-negative amount split on the decimal point, or `null` when the
 * string is not one.
 *
 * `null` rather than a throw: this is reached from `sortProfileRows` inside a render
 * `useMemo`, so throwing took the whole admin page into `FeatureErrorBoundary`'s
 * "Something went wrong" — and only once the reader pressed the Spend header, which
 * made it look unrelated to the data. The service's money pattern permits a leading
 * sign, so a contract-conforming `"+0.00"` was enough to do it. One row that cannot be
 * ordered now costs that row its position, not the page.
 */
function splitAmount(
  amount: string,
): { whole: string; fraction: string } | null {
  const trimmed = amount.trim();
  if (!/^\d*\.?\d*$/.test(trimmed) || trimmed === '' || trimmed === '.') {
    reportUnorderableAmount(amount);
    return null;
  }
  const [whole = '', fraction = ''] = trimmed.split('.');
  return { whole, fraction };
}

/**
 * Amounts already reported, so one bad row in a table the reader re-sorts is one line
 * in the console rather than a stream of them — `shared/utils/time.ts`'s
 * `reportUnparsable` has the same shape for the same reason.
 */
const reportedUnorderableAmounts = new Set<string>();

function reportUnorderableAmount(amount: string): void {
  if (reportedUnorderableAmounts.has(amount)) return;
  reportedUnorderableAmounts.add(amount);
  console.warn(
    `sortProfileRows orders spend as a padded string and needs a non-negative decimal; sorting this row last instead: ${JSON.stringify(amount)}`,
  );
}

/**
 * Which of two amounts cannot be ordered, as a comparison that sinks it (R-F3).
 *
 * `0` when both parse or neither does, leaving the ordering to `compareAmounts` or to
 * the tie-break. **Sign-independent on purpose**: the caller does not apply the
 * direction to this, so an unorderable row is last whether the column runs ascending
 * or descending — "I could not order this" is not a value that reverses.
 */
function unparseableLast(a: string, b: string): number {
  const leftOk = splitAmount(a) !== null;
  const rightOk = splitAmount(b) !== null;
  if (leftOk === rightOk) return 0;
  return leftOk ? -1 : 1;
}

/**
 * One column's order, or — without a sort — the default, name ascending
 * (data-model.md § 2, ui-contract.md "Address").
 *
 * Returns a new array and never mutates its input: the items it orders are the
 * query cache's own objects.
 */
export function sortProfileRows(
  items: readonly LlmProfileRow[],
  sort?: LlmProfilesSort,
): LlmProfileRow[] {
  const copy = [...items];
  // Absent means the default view, which is the order `GET /api/llm-profiles`
  // already shows the reader today.
  if (!sort) return copy.sort(byName);
  const sign = sort.dir === 'asc' ? 1 : -1;
  return copy.sort((a, b) => {
    // An amount that cannot be ordered goes last, **before** the sign is applied, so
    // it stays last in both directions (R-F3). Only the spend column has amounts; the
    // other five compare strings and numbers that always order.
    const unorderable =
      sort.key === 'spend'
        ? unparseableLast(a.spend.amount, b.spend.amount)
        : 0;
    // Ties read A to Z whichever way the column runs: a reversed tie-break would
    // reorder equal rows every time the direction flipped, which reads as the data
    // changing under the reader. Copied from `sortRows` in organization-admin.
    return unorderable || sign * compareBy(sort.key, a, b) || byName(a, b);
  });
}

/**
 * The deleted profiles placed among the live rows (spec 258 FR-004).
 *
 * After them, A to Z, except when the grid is sorted by spend: then a deleted
 * profile's spend is ordered with everyone else's, since that is the question the
 * column answers. The other columns describe a live profile (its provider, its model,
 * what uses it), so a deleted one has nothing to be ordered by there.
 *
 * `live` arrives already filtered and ordered; a deleted profile is matched by name
 * only, as it has no model and no provider.
 */
export function placeDeletedRows(
  live: readonly LlmProfileRow[],
  deleted: readonly DeletedLlmProfileRow[],
  query: string,
  sort?: LlmProfilesSort,
): LlmProfileGridEntry[] {
  const needle = query.trim().toLocaleLowerCase();
  const shown = deleted.filter((item) =>
    item.name.toLocaleLowerCase().includes(needle),
  );
  if (shown.length === 0) return [...live];
  if (sort?.key !== 'spend') return [...live, ...shown.sort(byName)];
  const sign = sort.dir === 'asc' ? 1 : -1;
  return [...live, ...shown].sort(
    (a, b) =>
      unparseableLast(a.spend.amount, b.spend.amount) ||
      sign * compareAmounts(a.spend.amount, b.spend.amount) ||
      byName(a, b),
  );
}

/** The one column's comparison, before the sign and the tie-break are applied. */
function compareBy(
  key: ProfileSortKey,
  a: LlmProfileRow,
  b: LlmProfileRow,
): number {
  switch (key) {
    case 'name':
      return byName(a, b);
    case 'provider':
      return a.ai_provider.name.localeCompare(b.ai_provider.name, undefined, {
        sensitivity: 'base',
      });
    case 'model':
      return a.model.localeCompare(b.model, undefined, {
        sensitivity: 'base',
      });
    case 'spend':
      return compareAmounts(a.spend.amount, b.spend.amount);
    case 'workspaces':
      return a.usage.workspace_count - b.usage.workspace_count;
    case 'workflows':
      return a.usage.workflow_count - b.usage.workflow_count;
  }
}

/**
 * Stand-in while the profile list that carries `model_status` is still loading
 * (the admin overview rows do not carry it). It shows no notice and no marker; it
 * is not a claim that the model is valid, and the backend still refuses an invalid
 * model on save.
 */
const STATUS_NOT_YET_KNOWN: LlmProfileModelStatus = {
  valid: true,
  expected_model: null,
  reason: null,
};

/**
 * An overview row as the plain `LlmProfile` the reused components already take
 * (FR-018a) — the single place the two endpoint shapes meet.
 *
 * Not optional polish: `LlmProfileFormSheet` reads `entry.ai_provider_id`
 * (`LlmProfileFormSheet.tsx:93` and `:183`) and an overview row carries the
 * provider as a nested object instead, so under `strict: true` the section does not
 * compile without this. Flattening here rather than widening or narrowing the three
 * components' props keeps those files — which SC-005 is judged by — untouched.
 */
export function toLlmProfile(
  item: LlmProfileRow,
  modelStatus: LlmProfileModelStatus = STATUS_NOT_YET_KNOWN,
): LlmProfile {
  return {
    slug: item.slug,
    name: item.name,
    ai_provider_id: item.ai_provider.id,
    model: item.model,
    effort: item.effort,
    max_turns: item.max_turns,
    description: item.description,
    is_default: item.is_default,
    created_at: item.created_at,
    updated_at: item.updated_at,
    model_status: modelStatus,
  };
}
